import * as React from 'react'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Badge } from '@/components/ui/badge'

export interface SearchableSelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface SearchableMultiSelectProps {
  options: SearchableSelectOption[]
  value?: string[]
  onValueChange?: (value: string[]) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  disabled?: boolean
  className?: string
  label?: string | React.ReactNode
  error?: boolean
  errorMessage?: string
  /** When provided, shown as first option in dropdown (e.g. "All departments"). Selecting it sets value to [allOption.value] only. */
  allOption?: { value: string; label: string }
}

export const SearchableMultiSelect = React.forwardRef<
  HTMLButtonElement,
  SearchableMultiSelectProps
>(
  (
    {
      options,
      value = [],
      onValueChange,
      placeholder = 'Select options...',
      searchPlaceholder = 'Search...',
      emptyMessage = 'No results found.',
      disabled = false,
      className,
      label,
      error = false,
      errorMessage,
      allOption,
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false)
    const isAllSelected = Boolean(allOption && value?.includes(allOption.value))

    // Create a stable map of label to value for reliable selection
    const labelToValueMap = React.useMemo(() => {
      const map = new Map<string, string>()
      if (allOption) {
        map.set(allOption.label, allOption.value)
      }
      options.forEach((option) => {
        if (option && option.label && typeof option.label === 'string') {
          map.set(option.label, option.value)
        }
      })
      return map
    }, [options, allOption])

    const selectedOptions = React.useMemo(() => {
      if (isAllSelected && allOption) {
        return [{ value: allOption.value, label: allOption.label }]
      }
      return options.filter((option) => value.includes(option.value))
    }, [options, value, isAllSelected, allOption])

    const handleSelect = (selectedValue: string) => {
      const currentValues = value || []
      if (allOption && selectedValue === allOption.value) {
        if (currentValues.includes(allOption.value)) {
          onValueChange?.([])
        } else {
          onValueChange?.([allOption.value])
        }
        return
      }
      if (currentValues.includes(selectedValue)) {
        onValueChange?.(currentValues.filter(v => v !== selectedValue))
      } else {
        // When adding a normal option, remove "All" if it was selected
        const withoutAll = allOption
          ? currentValues.filter(v => v !== allOption!.value)
          : currentValues
        onValueChange?.([...withoutAll, selectedValue])
      }
    }

    const handleRemove = (e: React.MouseEvent, valueToRemove: string) => {
      e.stopPropagation()
      const currentValues = value || []
      onValueChange?.(currentValues.filter(v => v !== valueToRemove))
    }

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation()
      onValueChange?.([])
    }

    return (
      <div className="space-y-2">
        {label && (
          <label className={cn('text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70', error && 'text-destructive')}>
            {label}
          </label>
        )}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              ref={ref}
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              className={cn(
                'w-full justify-between min-h-9 h-auto max-h-14',
                selectedOptions.length === 0 && 'text-muted-foreground',
                error && 'border-destructive',
                className
              )}
            >
              <div className="flex flex-wrap gap-1.5 items-center flex-1 min-w-0 text-left overflow-hidden">
                {selectedOptions.length === 0 ? (
                  <span className="text-muted-foreground truncate">{placeholder}</span>
                ) : (
                  selectedOptions.map((option) => (
                    <Badge
                      key={option.value}
                      variant="secondary"
                      className="shrink-0 max-w-full truncate"
                    >
                      <span className="truncate">{option.label}</span>
                      <button
                        type="button"
                        className="ml-1 shrink-0 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleRemove(e as any, option.value)
                          }
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                        }}
                        onClick={(e) => handleRemove(e, option.value)}
                      >
                        <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                      </button>
                    </Badge>
                  ))
                )}
              </div>
              <div className="flex items-center gap-1 ml-2">
                {value && value.length > 0 && !disabled && (
                  <X
                    className="h-4 w-4 shrink-0 opacity-50 hover:opacity-100"
                    onClick={handleClear}
                  />
                )}
                <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[250px] p-0" align="start">
            <Command>
              <CommandInput placeholder={searchPlaceholder} />
              <CommandList>
                <CommandEmpty>{emptyMessage}</CommandEmpty>
                <CommandGroup>
                  {allOption && (
                    <CommandItem
                      key={allOption.value}
                      value={allOption.label}
                      onSelect={() => {
                        handleSelect(allOption.value)
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          isAllSelected ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      {allOption.label}
                    </CommandItem>
                  )}
                  {options.filter(opt => opt && opt.label && opt.value).map((option) => {
                    const isSelected = value.includes(option.value)
                    return (
                      <CommandItem
                        key={`${option.value}-${option.label}`}
                        value={option.label || ''}
                        disabled={option.disabled}
                        onSelect={(selectedLabel) => {
                          if (!selectedLabel || typeof selectedLabel !== 'string') {
                            return
                          }
                          
                          const actualValue = labelToValueMap.get(selectedLabel) || 
                            Array.from(labelToValueMap.entries()).find(
                              ([label]) => label && typeof label === 'string' && label.toLowerCase() === selectedLabel.toLowerCase()
                            )?.[1]
                          
                          if (actualValue) {
                            handleSelect(actualValue)
                          } else {
                            const matchedOption = options.find(opt => opt && opt.label === selectedLabel)
                            if (matchedOption) {
                              handleSelect(matchedOption.value)
                            }
                          }
                        }}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            isSelected ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        {option.label}
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {error && errorMessage && (
          <p className="text-sm text-destructive mt-1">{errorMessage}</p>
        )}
      </div>
    )
  }
)

SearchableMultiSelect.displayName = 'SearchableMultiSelect'

